import { cn } from "@/lib/utils";

interface CornerAccentsProps {
	/**
	 * "top": rays fan up-and-out for the mockup's top-right outer corner.
	 * "bottom": rays fan down-and-out for the bottom-right outer corner.
	 */
	variant: "top" | "bottom";
	className?: string;
}

// A single hand-drawn 3-stroke fan (center stroke longest, sides shorter,
// slightly irregular spacing) in the same teal as the annotation and arrow.
// Both corners reuse it; only the group rotation differs so it radiates
// diagonally out of each corner.
const FAN = [
	// left: shorter, angled slightly outward-left
	"M11.5 25C11 21.5 10.6 18.8 10 16",
	// center: longest, near-vertical but tilted
	"M15.5 26C16.3 21 16.9 16.4 18 12",
	// right: shorter, angled outward-right
	"M20 25.5C21.7 23 23 20.8 24.8 18",
];

// The fan is rotated ~40° clockwise so it radiates diagonally up-and-right out
// of the top corner. The bottom corner uses the exact vertical mirror of that
// (flip around the viewBox mid-line, y=20) so it radiates down-and-right from
// its corner in a matching way.
const TRANSFORM: Record<CornerAccentsProps["variant"], string> = {
	top: "rotate(40 16 20)",
	bottom: "matrix(1 0 0 -1 0 40) rotate(40 16 20)",
};

/**
 * Small decorative hand-drawn accent marks that sit in the empty background
 * just outside the storefront mockup's right corners, echoing the annotation's
 * hand-drawn language. Purely decorative; hidden from assistive tech and
 * mirrored in RTL so they stay on the mockup's outer edge.
 */
function CornerAccents(prop: CornerAccentsProps) {
	const { variant, className } = prop;
	return (
		<svg
			aria-hidden
			className={cn("pointer-events-none absolute text-(--brand-1) rtl:-scale-x-100", className)}
			viewBox="0 0 34 40"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			{/* Fan is oriented so it radiates diagonally out of the corner. */}
			<g transform={TRANSFORM[variant]}>
				{FAN.map((d) => (
					<path key={d} d={d} />
				))}
			</g>
		</svg>
	);
}

export default CornerAccents;
